import React from "react";
import {
  useAppSelector,
  useAppDispatch,
  updateShipStats,
  setCurrentStep,
  setHull,
} from "../../store";
import { calculateShipStats } from "../../utils/calculations";
import {
  validateStep,
  getNextValidStep,
  getPreviousValidStep,
} from "../../utils/validation";
import { SHIP_BUILDER_STEPS, ShipBuilderStepId } from "../../constants/ship";
import { StepNavigation } from "./steps";
import { FinaliseDesignStep } from "./steps/FinaliseDesignStep";
import {
  ShipSizeSelector,
  HullSelector,
  DriveSelector,
  SensorsSelector,
  ShipStatsDisplay,
  HullArmorSummary,
  DriveSummary,
  BridgeSummary,
  BreakawayPowerPlantSummary,
  BreakawayFuelSummary,
} from "./components/core";
import FuelSelector from "./components/weapons/FuelSelector";
import CargoSelector from "./components/weapons/CargoSelector";
import { markStepVisited } from "../../store/features/shipBuilderSlice";
import { HULL_CONFIGURATIONS, calculateHullCost } from "../../constants/hull";
import {
  PowerPlantStep,
  BreakawayPowerPlantStep,
  BreakawayFuelStep,
  BridgeStep,
  ComputerStep,
  CrewStep,
  StateroomStep,
} from "./steps";
import WeaponsStep from "./steps/WeaponsStep";
import WeaponsFooterSummary from "./components/weapons/WeaponsFooterSummary";
import OptionalSystems from "./steps/OptionalSystems";
import ErrorBoundary from "./components/optional-systems/ErrorBoundary";
import { selectIsBreakaway } from "../../store/selectors/breakawayHullSelectors";
import { ShipMetadata } from "../react19/DocumentMetadata";
import { useExpensiveUpdate, usePerformanceMonitor } from "../../utils/react19/performance";
// Phase 3 features temporarily disabled for build
// import { AdvancedErrorBoundary } from "../react19/AdvancedSuspense";
// import { SkipNavigation } from "../react19/AccessibilityEnhancements";

/**
 * Main component for the ship builder interface
 * Manages the step-by-step process of building a ship and handles state updates
 *
 * @component
 * @example
 * ```tsx
 * <ShipBuilder />
 * ```
 */
export const ShipBuilder: React.FC = () => {
  const dispatch = useAppDispatch();
  const shipBuilderState = useAppSelector((state) => state.shipBuilder);
  const optionalSystems = useAppSelector((state) => state.optionalSystems);
  const currentStep = useAppSelector((state) => state.shipBuilder.currentStep);
  const isBreakaway = useAppSelector(selectIsBreakaway);

  // React 19 Performance Monitoring
  const performanceMonitor = usePerformanceMonitor('ShipBuilder');
  
  // React 19 Expensive Updates for step transitions
  const [, startExpensiveUpdate] = useExpensiveUpdate();

  // Add defensive programming to handle undefined SHIP_BUILDER_STEPS
  const currentStepData = SHIP_BUILDER_STEPS[currentStep];
  
  // If currentStepData is undefined, reset to step 0
  React.useEffect(() => {
    if (!currentStepData) {
      console.warn(`Invalid currentStep: ${currentStep}, resetting to 0`);
      dispatch(setCurrentStep(0));
      return;
    }
    
    dispatch(markStepVisited(currentStep));
    // Initialize hull state if entering Step 1 and hull is null
    if (
      currentStepData.id === "hull-and-armor" &&
      !shipBuilderState.hull
    ) {
      const defaultConfig = HULL_CONFIGURATIONS[0];
      const size = shipBuilderState.shipSize || 100;
      const hullPoints = Math.floor(size / defaultConfig.hullPointsPerTon);
      dispatch(
        setHull({
          size,
          type: defaultConfig.type,
          configuration: defaultConfig,
          cost: calculateHullCost(size, defaultConfig),
          weight: size,
          hullPoints,
        }),
      );
    }
  }, [currentStep, dispatch, shipBuilderState.hull, shipBuilderState.shipSize, currentStepData]);

  // React 19 Performance Monitoring cleanup
  React.useEffect(() => {
    return () => {
      performanceMonitor.endMonitoring();
    };
  }, [performanceMonitor]);

  /**
   * Handles moving to the next step in the ship building process
   * Updates ship stats before proceeding with React 19 transitions
   */
  const handleNext = () => {
    startExpensiveUpdate(() => {
      const stats = calculateShipStats(shipBuilderState, optionalSystems);
      if (stats.success && stats.data) {
        dispatch(updateShipStats(stats.data));
      }
      
      if (!currentStepData) {
        console.error("Cannot proceed: currentStepData is undefined");
        return;
      }
      
      const currentStepId = currentStepData.id as ShipBuilderStepId;
      const nextStepId = getNextValidStep(shipBuilderState, currentStepId);
      if (nextStepId !== null) {
        const nextStepIndex = SHIP_BUILDER_STEPS.findIndex(
          (step) => step.id === nextStepId,
        );
        if (nextStepIndex !== -1) {
          dispatch(setCurrentStep(nextStepIndex));
        }
      }
    });
  };

  /**
   * Handles moving to the previous step in the ship building process
   * Uses React 19 transitions for smooth navigation
   */
  const handlePrevious = () => {
    startExpensiveUpdate(() => {
      if (!currentStepData) {
        console.error("Cannot go back: currentStepData is undefined");
        return;
      }
      
      const currentStepId = currentStepData.id as ShipBuilderStepId;
      const previousStepId = getPreviousValidStep(shipBuilderState, currentStepId);
      if (previousStepId !== null) {
        const previousStepIndex = SHIP_BUILDER_STEPS.findIndex(
          (step) => step.id === previousStepId,
        );
        if (previousStepIndex !== -1) {
          dispatch(setCurrentStep(previousStepIndex));
        }
      }
    });
  };

  /**
   * Checks if the current step is complete based on validation rules
   */
  const isCurrentStepComplete = () => {
    if (!currentStepData) {
      return false;
    }
    
    const validation = validateStep(shipBuilderState, currentStepData.id);
    return validation.isValid;
  };

  /**
   * Renders the appropriate component based on the current step
   */
  const renderStepContent = () => {
    if (!currentStepData) {
      return <div>Error: Invalid step configuration</div>;
    }
    
    switch (currentStepData.id) {
      case "initial":
        return <ShipSizeSelector />;
      case "hull-and-armor":
        return <HullSelector />;
      case "drives":
        return <DriveSelector />;
      case "power":
        return isBreakaway ? <BreakawayPowerPlantStep /> : <PowerPlantStep />;
      case "fuel":
        return isBreakaway ? <BreakawayFuelStep /> : <FuelSelector />;
      case "bridge":
        return <BridgeStep />;
      case "computer":
        return <ComputerStep />;
      case "sensors":
        return <SensorsSelector />;
      case "weapons":
        return <WeaponsStep />;
      case "optional-systems":
        return <OptionalSystems />;
      case "crew":
        return <CrewStep />;
      case "staterooms":
        return <StateroomStep />;
      case "cargo":
        return <CargoSelector />;
      case "summary":
        return <FinaliseDesignStep />;
      default:
        return null;
    }
  };

  // If currentStepData is undefined, show loading or error state
  if (!currentStepData) {
    return (
      <div className="container mx-auto px-2 md:px-6 lg:px-12 max-w-screen-xl">
        <div className="w-full flex flex-col items-center mb-2">
          <h1
            className="text-4xl font-extrabold text-highlightOrange tracking-wide text-center"
            style={{ fontFamily: "Orbitron, sans-serif" }}
          >
            Mongoose Traveller 2e, High Guard Ship Builder
          </h1>
        </div>
        <div className="text-center text-red-600">
          Loading step configuration...
        </div>
      </div>
    );
  }

  const isHullAndArmorStep = currentStepData.id === "hull-and-armor";
  const isDrivesStep = currentStepData.id === "drives";
  const isPowerStep = currentStepData.id === "power";
  const isFuelStep = currentStepData.id === "fuel";
  const isBridgeStep = currentStepData.id === "bridge";
  const isWeaponsStep = currentStepData.id === "weapons";

  return (
    <>
      {/* React 19 Document Metadata */}
      <ShipMetadata
        shipName={shipBuilderState.finalDetails?.name}
        shipType={shipBuilderState.hull?.type}
        shipSize={shipBuilderState.shipSize}
        techLevel={shipBuilderState.techLevel}
      />
      
      <div className="container mx-auto px-2 md:px-6 lg:px-12 max-w-screen-xl">
      <div className="w-full flex flex-col items-center mb-2">
        <h1
          className="text-4xl font-extrabold text-highlightOrange tracking-wide text-center"
          style={{ fontFamily: "Orbitron, sans-serif" }}
        >
          Mongoose Traveller 2e, High Guard Ship Builder
        </h1>
        <div className="text-center mt-2">
          <span className="bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-sm font-semibold px-3 py-1 rounded-full border border-red-200 dark:border-red-700">
            BETA TEST Version 0.1.4
          </span>
        </div>
      </div>
      <div className="step-indicator bg-highlightOrange text-white font-bold py-2 px-4 rounded mb-4 shadow">
        {currentStepData.label}
      </div>
      <nav id="step-navigation">
        <StepNavigation />
      </nav>
      <div id="ship-stats">
        <ShipStatsDisplay />
      </div>
      <main id="main-content" className="pb-32">
        <ErrorBoundary>{renderStepContent()}</ErrorBoundary>
      </main>
      <div className="navigation-buttons flex gap-4 mt-6">
        {currentStep > 0 && (
          <button
            className="bg-paleBlue dark:bg-blue-800 text-gray-900 dark:text-gray-100 font-semibold py-2 px-6 rounded shadow hover:bg-blue-200 dark:hover:bg-blue-700 transition"
            onClick={handlePrevious}
          >
            Previous
          </button>
        )}
        {currentStep < SHIP_BUILDER_STEPS.length - 1 && (
          <button
            className={`font-semibold py-2 px-6 rounded shadow transition ${
              isCurrentStepComplete()
                ? "bg-highlightOrange text-white hover:bg-orange-400"
                : "bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
            }`}
            onClick={handleNext}
            disabled={!isCurrentStepComplete()}
          >
            Next
          </button>
        )}
      </div>
      {isHullAndArmorStep && (
        <div className="fixed bottom-0 left-0 right-0 z-10">
          <HullArmorSummary />
        </div>
      )}
      {isDrivesStep && !isBreakaway && (
        <div className="fixed bottom-0 left-0 right-0 z-10">
          <DriveSummary />
        </div>
      )}
      {isPowerStep && isBreakaway && (
        <div className="fixed bottom-0 left-0 right-0 z-10">
          <BreakawayPowerPlantSummary />
        </div>
      )}
      {isFuelStep && isBreakaway && (
        <div className="fixed bottom-0 left-0 right-0 z-10">
          <BreakawayFuelSummary />
        </div>
      )}
      {isBridgeStep && (
        <div className="fixed bottom-0 left-0 right-0 z-10">
          <BridgeSummary />
        </div>
      )}
      {isWeaponsStep && (
        <div className="fixed bottom-0 left-0 right-0 z-10">
          <WeaponsFooterSummary />
        </div>
      )}
      </div>
    </>
  );
};
